import type { Les } from '../types';
import type { LesNu } from '../lib/roosterItems';
import { badgesVoorLes } from '../lib/badges';
import { duurMinuten } from '../lib/tijd';
import { MetaRegel, uurLabel, uurNummer } from './MetaRegel';

/**
 * Tot deze lengte past een lokaalnaam op de grootste trap ('B09', 'Aula'); daarboven
 * ('Sportzaal 1') zou hij over twee regels breken en de kaart uit elkaar duwen.
 */
const LOKAAL_KORT = 5;

interface Props {
  les: Les;
  /** Gevuld als deze les op dit moment bezig is — zie lib/roosterItems. */
  nu?: LesNu | null;
  /** De lichtblauwe rij in de wit/lichtblauw-afwisseling. */
  zebra?: boolean;
}

export function LesKaart({ les, nu = null, zebra = false }: Props) {
  const lokaalWijziging = les.wijzigingen.find((w) => w.veld === 'lokaal');
  const lokaal = lokaalWijziging ? lokaalWijziging.nieuw : les.lokalen.join(', ');
  const duur = duurMinuten(les.startTijd, les.eindTijd);
  const uur = uurNummer(les.uur, les.uurTot);
  const badges = badgesVoorLes(les);

  return (
    <div className={`lesson-card status-${les.status}${zebra ? ' zebra' : ''}${nu ? ' nu' : ''}`}>
      {/* Begin- én eindtijd: op een wandtablet is "tot hoe laat duurt dit" net zo
          vaak de vraag als "hoe laat begint het" — bij een blokuur of een toets is
          dat het verschil tussen één en twee uur zaal. De lesduur staat erbij omdat
          je die anders zelf uit twee kloktijden moet aftrekken. */}
      {/* Leeg bij een activiteit die niet op een lesuur valt; de kolom blijft, zodat
          de tijden eronder recht onder elkaar blijven staan. */}
      <div className="uur-col">
        {uur && (
          <span
            className={uur.length > 2 ? 'uur-bol blok' : 'uur-bol'}
            aria-label={uurLabel(les.uur, les.uurTot)}
          >
            {uur}
          </span>
        )}
      </div>

      <div className="time-col">
        <div className="tijd">{les.startTijd}</div>
        <div className="tot">
          {les.eindTijd}
          {duur > 0 && ` · ${duur} m`}
        </div>
      </div>

      <div className="subject-block">
        <div className="subject">
          <span className="vak">{les.vak}</span>
          {nu && <span className="nu-badge">{`NU · NOG ${nu.restMinuten} MIN`}</span>}
        </div>
        <MetaRegel les={les} />
      </div>

      <div className={lokaal.length > LOKAAL_KORT ? 'room-col lang' : 'room-col'}>
        {lokaalWijziging && <><s className="old-val">{lokaalWijziging.oud}</s><span aria-hidden="true"> → </span></>}
        {lokaal && <span className="room">{lokaal}</span>}
      </div>

      {badges.length > 0 && (
        <div className="lesson-badges">
          {badges.map((badge) => (
            <span key={badge.tekst} className={`badge status-${badge.kleur}`}>
              {badge.tekst}
            </span>
          ))}
        </div>
      )}

      {/* Hoe ver deze les is, als balkje over de onderrand — hetzelfde groen als de
          tijdlijn links, zodat "nu" op de kaart en "nu" in de dag dezelfde kleur
          hebben. */}
      {nu && <span className="les-voortgang" style={{ width: `${nu.voortgang * 100}%` }} />}
    </div>
  );
}
